import { useState } from 'react';
import { authApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { Inp } from '../../components/ui/Inputs.jsx';
import { C } from '../../theme/palette';

/** Longueur du code affiché par l'application. */
const LONGUEUR_CODE = 6;

/** « JBSWY3DPEHPK3PXP » devient « JBSW Y3DP EHPK 3PXP », plus facile à recopier. */
export function cleLisible(secret) {
  return String(secret || '')
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

const etape = { display: 'flex', gap: 10, marginBottom: 14, alignItems: 'flex-start' };
const numero = {
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: C.navy,
  color: '#fff',
  fontSize: 11,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/**
 * Relie une application d'authentification (Google Authenticator, Microsoft
 * Authenticator…) au compte, une seule fois : le mot de passe, puis le code QR
 * à scanner, puis le premier code affiché par l'application.
 *
 * <p>Tant que ce premier code n'est pas juste, rien ne change à la connexion :
 * une liaison abandonnée ne laisse personne dehors.
 *
 * @param {object} props propriétés
 * @param {Function} props.onRelie appelée avec l'état du serveur une fois l'application reliée
 * @returns {JSX.Element} les étapes
 */
export function RelierApplication({ onRelie }) {
  const [motDePasse, setMotDePasse] = useState('');
  const [cle, setCle] = useState(null);
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const tente = async (action) => {
    setErreur('');
    setEnvoi(true);
    try {
      await action();
    } catch (e) {
      setErreur(e?.message || 'La demande n’a pas abouti.');
    } finally {
      setEnvoi(false);
    }
  };

  const afficheLeCode = () =>
    tente(async () => {
      const reponse = await authApi.relieApplication(motDePasse);
      setCle(reponse);
      setMotDePasse('');
      setCode('');
    });

  const active = () =>
    tente(async () => {
      const etat = await authApi.confirmeApplication(code);
      onRelie?.(etat);
    });

  const alerte = erreur && (
    <div role="alert" style={{ fontSize: 10.5, color: C.red, fontWeight: 700, marginTop: 8 }}>
      {erreur}
    </div>
  );

  if (!cle) {
    return (
      <div>
        <Inp
          label="Mot de passe actuel"
          type="password"
          value={motDePasse}
          onChange={(e) => setMotDePasse(e.target.value)}
        />
        <Btn
          label={envoi ? 'Un instant…' : 'Afficher le code QR'}
          color={C.teal}
          full
          disabled={envoi || !motDePasse}
          onClick={afficheLeCode}
        />
        {alerte}
      </div>
    );
  }

  return (
    <div style={{ fontSize: 12, lineHeight: 1.5, color: C.ink }}>
      <div style={etape}>
        <div style={numero}>1</div>
        <div>
          Installe <b>Google Authenticator</b> (App Store ou Google Play) si tu ne l’as pas déjà.
        </div>
      </div>
      <div style={etape}>
        <div style={numero}>2</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          Dans l’appli, touche <b>« + »</b> puis <b>« Scanner un code QR »</b> :
          <div
            data-testid="qr-application"
            style={{ display: 'flex', justifyContent: 'center', margin: '10px 0' }}
          >
            <div
              style={{
                background: '#fff',
                padding: 8,
                border: '1px solid ' + C.border,
                borderRadius: 8,
              }}
            >
              {/* Dessiné par le serveur, comme le QR-facture : un code QR
                  qu'aucune application ne lirait laisserait la personne dehors. */}
              <img src={cle.qr} alt="QR" width={170} height={170} />
            </div>
          </div>
          <a
            href={cle.adresse}
            style={{
              display: 'block',
              textAlign: 'center',
              border: '1.5px solid ' + C.border,
              borderRadius: 10,
              padding: '10px 8px',
              fontWeight: 700,
              color: C.ink,
              textDecoration: 'none',
              fontSize: 12,
            }}
          >
            📲 Je suis sur ce téléphone : ajouter à Authenticator
          </a>
          <div style={{ fontSize: 10.5, color: C.muted, marginTop: 7, textAlign: 'center' }}>
            ou tape la clé :{' '}
            <b style={{ letterSpacing: 1, color: C.ink, overflowWrap: 'anywhere' }}>
              {cleLisible(cle.secret)}
            </b>
          </div>
        </div>
      </div>
      <div style={etape}>
        <div style={numero}>3</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          Tape le code affiché sous <b>SwissTech</b> dans l’appli :
          <div style={{ marginTop: 8 }}>
            <Inp
              label="Code de l’application"
              placeholder="6 chiffres"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, LONGUEUR_CODE))}
            />
          </div>
        </div>
      </div>
      <Btn
        label={envoi ? 'Vérification…' : '✓ Activer'}
        color={C.teal}
        full
        disabled={envoi || code.length !== LONGUEUR_CODE}
        onClick={active}
      />
      {alerte}
    </div>
  );
}
